/**
 * Atmosphere — fog, light, and weather as a GENERATIVE FAMILY (user
 * direction). A mood table defines the base combos; every endless ZONE
 * (a run of segments) draws a mood + parameter jitter + a weather form
 * from the mood's allowed set, all label-derived from the seed
 * (`atmosphere/zone/${z}`). Crossing a zone boundary crossfades every
 * scalar (sky, fog, sun color/intensity/angle, hemisphere, starfield) over
 * a few seconds and swaps the weather field with a fade — the void has
 * climate now.
 *
 * Presentation-only: reads the seed through its own rng family, never
 * perturbs generation. Weather fields are camera-local particle volumes
 * (snowfall, drifting dust, rising embers) that wrap around the viewer.
 */
import * as THREE from "three";
import type { SceneCtx } from "./scene.ts";
import {
  drawZone,
  MOODS,
  type MoodSpec,
  type WeatherKind,
  type ZoneAtmosphere,
} from "../gen/climate.ts";

// The climate tables + zone draw moved to src/gen/climate.ts (M21):
// weather is a legal generation input now. Re-exported so client call
// sites keep their import paths.
export { drawZone, MOODS };
export type { MoodSpec, WeatherKind, ZoneAtmosphere };

// ---------------------------------------------------------------------------
// Weather fields — camera-local particle volumes
// ---------------------------------------------------------------------------

const WEATHER_BOX = 42;
const WEATHER_COUNT = 700;

class WeatherField {
  readonly points: THREE.Points;
  private readonly velocities: Float32Array;
  private readonly phases: Float32Array;
  private readonly material: THREE.PointsMaterial;
  private targetOpacity: number;
  private t = 0;

  constructor(readonly kind: Exclude<WeatherKind, "none">, color: number) {
    const positions = new Float32Array(WEATHER_COUNT * 3);
    this.velocities = new Float32Array(WEATHER_COUNT * 3);
    this.phases = new Float32Array(WEATHER_COUNT);
    for (let i = 0; i < WEATHER_COUNT; i++) {
      for (let a = 0; a < 3; a++) {
        positions[i * 3 + a] = (Math.random() - 0.5) * WEATHER_BOX;
      }
      this.phases[i] = Math.random() * Math.PI * 2;
      const v = kind === "snow"
        ? [0, -1.1 - Math.random() * 0.6, 0]
        : kind === "dust"
        ? [0.14 + Math.random() * 0.1, -0.04, 0.1 * (Math.random() - 0.3)]
        : [0.03, 0.42 + Math.random() * 0.3, 0.02];
      this.velocities.set(v, i * 3);
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    this.targetOpacity = kind === "dust" ? 0.45 : 0.8;
    this.material = new THREE.PointsMaterial({
      color,
      size: kind === "snow" ? 0.055 : kind === "embers" ? 0.045 : 0.03,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.points = new THREE.Points(geom, this.material);
    this.points.name = `weather:${kind}`;
    this.points.frustumCulled = false;
  }

  /** dying → fade out; returns true when fully faded and disposable. */
  update(dt: number, camPos: THREE.Vector3, dying: boolean): boolean {
    this.t += dt;
    const target = dying ? 0 : this.targetOpacity;
    this.material.opacity += (target - this.material.opacity) *
      Math.min(1, dt * 1.5);
    this.points.position.copy(camPos);

    const pos = this.points.geometry.getAttribute(
      "position",
    ) as THREE.BufferAttribute;
    const arr = pos.array as Float32Array;
    const half = WEATHER_BOX / 2;
    for (let i = 0; i < WEATHER_COUNT; i++) {
      const sway = this.kind === "snow"
        ? Math.sin(this.t * 1.7 + this.phases[i]) * 0.35
        : this.kind === "embers"
        ? Math.sin(this.t * 2.3 + this.phases[i]) * 0.12
        : Math.sin(this.t * 0.7 + this.phases[i]) * 0.2;
      arr[i * 3] += (this.velocities[i * 3] + sway * 0.4) * dt;
      arr[i * 3 + 1] += this.velocities[i * 3 + 1] * dt;
      arr[i * 3 + 2] += (this.velocities[i * 3 + 2] + sway * 0.25) * dt;
      // Wrap within the camera-local box.
      for (let a = 0; a < 3; a++) {
        const v = arr[i * 3 + a];
        if (v > half) arr[i * 3 + a] = v - WEATHER_BOX;
        else if (v < -half) arr[i * 3 + a] = v + WEATHER_BOX;
      }
    }
    pos.needsUpdate = true;
    return dying && this.material.opacity < 0.015;
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.points);
    this.points.geometry.dispose();
    this.material.dispose();
  }
}

// ---------------------------------------------------------------------------
// The blender
// ---------------------------------------------------------------------------

const BLEND_SECONDS = 4;

interface Snapshot {
  sky: THREE.Color;
  fogNear: number;
  fogFar: number;
  sunColor: THREE.Color;
  sunIntensity: number;
  sunDir: THREE.Vector3;
  hemiSky: THREE.Color;
  hemiGround: THREE.Color;
  hemiIntensity: number;
  starColor: THREE.Color;
  starOpacity: number;
}

function snapshotOf(z: ZoneAtmosphere): Snapshot {
  return {
    sky: new THREE.Color(z.mood.sky),
    fogNear: z.fogNear,
    fogFar: z.fogFar,
    sunColor: new THREE.Color(z.mood.sunColor),
    sunIntensity: z.sunIntensity,
    sunDir: new THREE.Vector3(...z.mood.sunDir).normalize(),
    hemiSky: new THREE.Color(z.mood.hemiSky),
    hemiGround: new THREE.Color(z.mood.hemiGround),
    hemiIntensity: z.mood.hemiIntensity,
    starColor: new THREE.Color(z.mood.starColor),
    starOpacity: z.mood.starOpacity,
  };
}

function lerpSnap(a: Snapshot, b: Snapshot, t: number, out: Snapshot): void {
  out.sky.lerpColors(a.sky, b.sky, t);
  out.fogNear = a.fogNear + (b.fogNear - a.fogNear) * t;
  out.fogFar = a.fogFar + (b.fogFar - a.fogFar) * t;
  out.sunColor.lerpColors(a.sunColor, b.sunColor, t);
  out.sunIntensity = a.sunIntensity + (b.sunIntensity - a.sunIntensity) * t;
  out.sunDir.lerpVectors(a.sunDir, b.sunDir, t).normalize();
  out.hemiSky.lerpColors(a.hemiSky, b.hemiSky, t);
  out.hemiGround.lerpColors(a.hemiGround, b.hemiGround, t);
  out.hemiIntensity = a.hemiIntensity +
    (b.hemiIntensity - a.hemiIntensity) * t;
  out.starColor.lerpColors(a.starColor, b.starColor, t);
  out.starOpacity = a.starOpacity + (b.starOpacity - a.starOpacity) * t;
}

export class Atmosphere {
  private from: Snapshot;
  private to: Snapshot;
  private cur: Snapshot;
  private blend = 1;
  private fog: THREE.Fog;
  private weather: WeatherField | null = null;
  private dyingWeather: WeatherField[] = [];
  private currentName: string;
  private currentWeather: WeatherKind;

  constructor(
    private readonly ctx: SceneCtx,
    private readonly stars: THREE.Points,
    initial: ZoneAtmosphere,
  ) {
    this.from = snapshotOf(initial);
    this.to = snapshotOf(initial);
    this.cur = snapshotOf(initial);
    this.currentName = initial.mood.name;
    this.currentWeather = initial.weather;
    this.fog = new THREE.Fog(0x000000, initial.fogNear, initial.fogFar);
    ctx.scene.fog = this.fog;
    ctx.scene.background = new THREE.Color(initial.mood.sky);
    this.setWeather(initial);
    this.apply();
  }

  get name(): string {
    return this.currentName;
  }

  get weatherKind(): WeatherKind {
    return this.currentWeather;
  }

  blendTo(zone: ZoneAtmosphere): void {
    this.from = { ...this.cur, ...cloneColors(this.cur) };
    this.to = snapshotOf(zone);
    this.blend = 0;
    this.currentName = zone.mood.name;
    this.setWeather(zone);
  }

  private setWeather(zone: ZoneAtmosphere): void {
    this.currentWeather = zone.weather;
    if (this.weather) {
      if (this.weather.kind === zone.weather) return;
      this.dyingWeather.push(this.weather);
      this.weather = null;
    }
    if (zone.weather !== "none") {
      this.weather = new WeatherField(
        zone.weather,
        zone.mood.weatherColor ?? 0xffffff,
      );
      this.ctx.scene.add(this.weather.points);
    }
  }

  /** Per-frame: advance blends + weather, aim the sun at the focus. */
  update(
    dt: number,
    focus: { center: THREE.Vector3; radius: number } | null,
  ): void {
    if (this.blend < 1) {
      this.blend = Math.min(1, this.blend + dt / BLEND_SECONDS);
      lerpSnap(this.from, this.to, smooth(this.blend), this.cur);
      this.apply();
    }
    if (focus) {
      this.ctx.sun.position.copy(focus.center).addScaledVector(
        this.cur.sunDir,
        focus.radius * 1.6 + 8,
      );
      this.ctx.sun.target.position.copy(focus.center);
    }
    const cam = this.ctx.camera.position;
    if (this.weather) this.weather.update(dt, cam, false);
    this.dyingWeather = this.dyingWeather.filter((w) => {
      if (!w.update(dt, cam, true)) return true;
      w.dispose(this.ctx.scene);
      return false;
    });
  }

  private apply(): void {
    const c = this.cur;
    (this.ctx.scene.background as THREE.Color).copy(c.sky);
    this.fog.color.copy(c.sky);
    this.fog.near = c.fogNear;
    this.fog.far = c.fogFar;
    this.ctx.sun.color.copy(c.sunColor);
    this.ctx.sun.intensity = c.sunIntensity;
    this.ctx.hemi.color.copy(c.hemiSky);
    this.ctx.hemi.groundColor.copy(c.hemiGround);
    this.ctx.hemi.intensity = c.hemiIntensity;
    const starMat = this.stars.material as THREE.PointsMaterial;
    starMat.color.copy(c.starColor);
    starMat.opacity = c.starOpacity;
  }

  dispose(): void {
    if (this.weather) this.weather.dispose(this.ctx.scene);
    for (const w of this.dyingWeather) w.dispose(this.ctx.scene);
    this.weather = null;
    this.dyingWeather = [];
  }
}

function cloneColors(s: Snapshot): Partial<Snapshot> {
  return {
    sky: s.sky.clone(),
    sunColor: s.sunColor.clone(),
    hemiSky: s.hemiSky.clone(),
    hemiGround: s.hemiGround.clone(),
    starColor: s.starColor.clone(),
    sunDir: s.sunDir.clone(),
  };
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}
